import { errorMsg } from '../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Alert,
  IconButton,
  Chip
} from '@mui/material';
import { Edit, Delete, Add, ArrowBack, CheckCircle, Cancel } from '@mui/icons-material';
import Header from '../components/Header';
import ContextNavBar from '../components/ContextNavBar';
import Footer from '../components/Footer';
import api from '../axios';
import AppLayout from '../components/AppLayout';

function Usuarios() {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  
  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [usuarioEditando, setUsuarioEditando] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    tipo: 'admin'
  });

  const carregarUsuarios = async () => {
    setLoading(true);
    try {
      const response = await api.get('/usuarios/');
      setUsuarios(response.data);
    } catch (err) {
      setErro('Erro ao conectar com o servidor');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    setSucesso('');
    
    try {
      if (usuarioEditando) {
        await api.put(`/usuarios/${usuarioEditando.id}`, formData);
        setSucesso('Usuário atualizado!');
      } else {
        await api.post('/usuarios/', formData);
        setSucesso('Usuário criado!');
      }
      setOpenModal(false);
      limparForm();
      carregarUsuarios();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao salvar usuário');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este usuário?')) return;
    
    try {
      await api.delete(`/usuarios/${id}`);
      setSucesso('Usuário deletado!');
      carregarUsuarios();
    } catch (err) {
      setErro('Erro ao deletar usuário');
    }
  };

  const handleAtivarDesativar = async (id, nomeUsuario) => {
    try {
      const response = await api.patch(`/usuarios/${id}/ativar`);
      setSucesso(`Usuário ${nomeUsuario} ${response.data.ativo ? 'ATIVADO' : 'DESATIVADO'} com sucesso!`);
      carregarUsuarios();
    } catch (err) {
      setErro('Erro ao atualizar status do usuário');
    }
  };

  const abrirModalEdicao = (usuario) => {
    setUsuarioEditando(usuario);
    setFormData({
      nome: usuario.nome,
      email: usuario.email,
      senha: '', // Não carregar senha por segurança
      tipo: usuario.tipo
    });
    setOpenModal(true);
  };

  const abrirModalCriacao = () => {
    setUsuarioEditando(null);
    limparForm();
    setOpenModal(true);
  };

  const limparForm = () => {
    setFormData({
      nome: '',
      email: '',
      senha: '',
      tipo: 'admin'
    });
  };

  const fecharModal = () => {
    setOpenModal(false);
    setUsuarioEditando(null);
    limparForm();
  };

  useEffect(() => {
    carregarUsuarios();
  }, []);

  // Verificar se não está logado ou não é admin
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
    }
  }, []);

  return (
    <>
      <AppLayout>
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={() => navigate('/')} 
              sx={{ mr: 2 }}
              title="Voltar à página inicial"
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
              Gerenciar Usuários
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={abrirModalCriacao}
          >
            Novo Usuário
          </Button>
        </Box>

        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
        {sucesso && <Alert severity="success" sx={{ mb: 2 }}>{sucesso}</Alert>}

        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>ID</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Nome</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>E-mail</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Tipo</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }} align="center">Status</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usuarios.map((usuario) => (
                <TableRow key={usuario.id}>
                  <TableCell>{usuario.id}</TableCell>
                  <TableCell>{usuario.nome}</TableCell>
                  <TableCell>{usuario.email}</TableCell>
                  <TableCell>{usuario.tipo}</TableCell>
                  <TableCell align="center">
                    {usuario.tipo === 'admin' || usuario.tipo === 'admin_tenant' ? (
                      <Chip label="ADMIN" color="primary" size="small" />
                    ) : usuario.ativo ? (
                      <Chip label="ATIVO" color="success" size="small" icon={<CheckCircle />} />
                    ) : (
                      <Chip label="PENDENTE" color="warning" size="small" icon={<Cancel />} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton 
                      color="primary" 
                      onClick={() => abrirModalEdicao(usuario)}
                      size="small"
                      title="Editar usuário"
                    >
                      <Edit />
                    </IconButton>
                    {usuario.tipo !== 'admin' && usuario.tipo !== 'admin_tenant' && (
                      <IconButton 
                        color={usuario.ativo ? "warning" : "success"}
                        onClick={() => handleAtivarDesativar(usuario.id, usuario.nome)}
                        size="small"
                        title={usuario.ativo ? "Desativar usuário" : "Ativar usuário"}
                      >
                        {usuario.ativo ? <Cancel /> : <CheckCircle />}
                      </IconButton>
                    )}
                    <IconButton 
                      color="error" 
                      onClick={() => handleDelete(usuario.id)}
                      size="small"
                      title="Deletar usuário"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Modal de Criação/Edição */}
        <Dialog open={openModal} onClose={fecharModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            {usuarioEditando ? 'Editar Usuário' : 'Novo Usuário'}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Nome"
                fullWidth
                variant="outlined"
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label="E-mail"
                type="email"
                fullWidth
                variant="outlined"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label={usuarioEditando ? "Nova Senha (deixe vazio para manter)" : "Senha"}
                type="password"
                fullWidth
                variant="outlined"
                value={formData.senha}
                onChange={(e) => setFormData({...formData, senha: e.target.value})}
                required={!usuarioEditando}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth variant="outlined">
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={formData.tipo}
                  onChange={(e) => setFormData({...formData, tipo: e.target.value})}
                  label="Tipo"
                  required
                >
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="professor">Professor</MenuItem>
                  <MenuItem value="aluno">Aluno</MenuItem>
                  <MenuItem value="consolidador">Consolidador</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={fecharModal}>Cancelar</Button>
              <Button type="submit" variant="contained">
                {usuarioEditando ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
      </AppLayout>
    </>
  );
}

export default Usuarios;